import React from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import {
  List,
  SearchInput,
  Show,
  ShowButton,
  SimpleShowLayout,
  TextField as RaTextField,
  TopToolbar,
  useListContext,
  useRecordContext,
  useRefresh,
} from "react-admin";
import { useSearchParams } from "react-router-dom";

const formatBytes = (value) => {
  const size = Number(value || 0);
  if (!size) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const exponent = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
  const formatted = size / 1024 ** exponent;
  return `${formatted.toFixed(formatted >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

const IMAGE_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "bmp",
  "svg",
  "heic",
  "heif",
  "avif",
]);

const isImageRecord = (record) => {
  if (record?.is_folder) return false;
  const contentType = String(record?.content_type || "").toLowerCase();
  if (contentType.startsWith("image/")) return true;
  const key = String(record?.full_path || record?.name || "").toLowerCase();
  const ext = key.includes(".") ? key.split(".").pop() : "";
  return IMAGE_EXTENSIONS.has(ext);
};

const BUNNY_BROWSER_FILTERS = [
  <SearchInput
    key="bunny-browser-search"
    source="q"
    alwaysOn
    placeholder="Search current folder"
    variant="outlined"
  />,
];

const BunnyBrowserActions = () => {
  const refresh = useRefresh();

  return (
    <TopToolbar sx={{ gap: 1 }}>
      <Button variant="outlined" onClick={() => refresh()}>
        Refresh
      </Button>
    </TopToolbar>
  );
};

const BunnyBrowserHeader = ({ zone, total }) => (
  <Card elevation={0} sx={{ mb: 2, border: "1px solid #d8e6e6" }}>
    <CardContent>
      <Stack spacing={1.25}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CloudOutlinedIcon sx={{ color: "#007073" }} />
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#007073" }}>
            Bunny Storage Browser
          </Typography>
        </Box>
        <Typography sx={{ color: "#424242" }}>
          Hierarchical listing of the configured Bunny Storage zone.
          Click a folder to descend; use the breadcrumb to navigate up.
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Chip label={`Zone ${zone || "-"}`} sx={{ backgroundColor: "#d8e6e6" }} />
          <Chip label={`Items ${total ?? 0}`} sx={{ backgroundColor: "#e8f4f4" }} />
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const BunnyBrowserDetails = ({ zone, storageEndpoint, cdnHostname }) => (
  <Card elevation={0} sx={{ mb: 2, border: "1px solid #d8e6e6" }}>
    <CardContent>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <TextField
          label="Storage Zone"
          value={zone || ""}
          fullWidth
          size="small"
          InputProps={{ readOnly: true }}
        />
        <TextField
          label="Storage Endpoint"
          value={storageEndpoint || ""}
          fullWidth
          size="small"
          InputProps={{ readOnly: true }}
        />
        <TextField
          label="CDN Hostname"
          value={cdnHostname || ""}
          fullWidth
          size="small"
          InputProps={{ readOnly: true }}
        />
      </Stack>
    </CardContent>
  </Card>
);

const BunnyBreadcrumbs = ({ crumbs, onNavigate }) => {
  const items = Array.isArray(crumbs) && crumbs.length ? crumbs : [{ name: "/", path: "" }];

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
        <Breadcrumbs separator="›" aria-label="bunny path">
          {items.map((crumb, index) => {
            const isLast = index === items.length - 1;
            const label = crumb.name === "/" ? "Root" : crumb.name;
            if (isLast) {
              return (
                <Typography
                  key={`${crumb.path}-last`}
                  sx={{ fontSize: 14, fontWeight: 700, color: "#007073" }}
                >
                  {label}
                </Typography>
              );
            }
            return (
              <MuiLink
                key={crumb.path || "root"}
                component="button"
                type="button"
                underline="hover"
                onClick={() => onNavigate(crumb.path || "")}
                sx={{ fontSize: 14, color: "#424242" }}
              >
                {label}
              </MuiLink>
            );
          })}
        </Breadcrumbs>
      </CardContent>
    </Card>
  );
};

const BunnyObjectCard = ({ record, onOpenFolder }) => {
  const isFolder = Boolean(record?.is_folder);
  const isImage = !isFolder && isImageRecord(record);
  const previewUrl = record?.effective_url || record?.public_url || "";

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        cursor: isFolder ? "pointer" : "default",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": { transform: "translateY(-2px)", boxShadow: "0 12px 24px rgba(0,112,115,0.08)" },
      }}
      onClick={() => {
        if (isFolder) onOpenFolder(record.full_path);
      }}
    >
      <CardContent>
        <Stack spacing={1.25}>
          <Box
            sx={{
              width: "100%",
              minHeight: 160,
              borderRadius: 2,
              border: "1px solid #d8e6e6",
              backgroundColor: "#f3f9f9",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 1,
            }}
          >
            {isFolder ? (
              <FolderOutlinedIcon sx={{ fontSize: 56, color: "#007073" }} />
            ) : isImage && previewUrl ? (
              <Box
                component="img"
                src={previewUrl}
                alt={record?.name || record?.full_path}
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <InsertDriveFileOutlinedIcon sx={{ fontSize: 46, color: "#007073" }} />
            )}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#262626", wordBreak: "break-word" }}>
              {record?.name || record?.full_path}
            </Typography>
            <Typography sx={{ mt: 0.25, fontSize: 12, color: "#666", wordBreak: "break-word" }}>
              {record?.full_path}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {isFolder ? (
              <Chip label="Folder" size="small" sx={{ backgroundColor: "#d8e6e6" }} />
            ) : (
              <>
                <Chip label={formatBytes(record?.size_bytes)} size="small" sx={{ backgroundColor: "#d8e6e6" }} />
                {record?.content_type ? (
                  <Chip label={record.content_type} size="small" sx={{ backgroundColor: "#f3f9f9" }} />
                ) : null}
                {record?.size_bytes === 0 ? (
                  <Chip label="Zero-byte" size="small" sx={{ backgroundColor: "#fff1ec", color: "#731d00" }} />
                ) : null}
              </>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 0.75, justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: 11, color: "#666" }}>
              {formatDateTime(record?.last_modified)}
            </Typography>
            {!isFolder ? (
              <ShowButton resource="admin/bunny-browser" record={record} label="Details" />
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const BunnyBrowserGrid = ({ records, onOpenFolder }) => {
  const { isLoading } = useListContext();

  if (isLoading) {
    return <Typography sx={{ color: "#4b5563" }}>Loading Bunny objects...</Typography>;
  }

  if (!records.length) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography sx={{ color: "#4b5563" }}>
            This folder is empty.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" },
      }}
    >
      {records.map((record) => (
        <Box key={record.id}>
          <BunnyObjectCard record={record} onOpenFolder={onOpenFolder} />
        </Box>
      ))}
    </Box>
  );
};

const BunnyBrowserListContent = ({ onNavigate }) => {
  const { data = [], total, filterValues = {} } = useListContext();
  const meta = data?.[0]?.__meta || {};
  const zone = meta.zone || "";
  const storageEndpoint = meta.storage_endpoint || "";
  const cdnHostname = meta.cdn_hostname || "";
  const crumbs = meta.breadcrumbs && meta.breadcrumbs.length ? meta.breadcrumbs : filterValues.__breadcrumbs || [];

  return (
    <>
      <BunnyBrowserHeader zone={zone} total={total} />
      <BunnyBrowserDetails
        zone={zone}
        storageEndpoint={storageEndpoint}
        cdnHostname={cdnHostname}
      />
      <BunnyBreadcrumbs crumbs={crumbs} onNavigate={onNavigate} />
      <BunnyBrowserGrid records={data} onOpenFolder={onNavigate} />
    </>
  );
};

export const BunnyBrowserList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const path = searchParams.get("path") || "";

  const onNavigate = React.useCallback(
    (nextPath) => {
      const params = new URLSearchParams(searchParams);
      if (nextPath) {
        params.set("path", nextPath);
      } else {
        params.delete("path");
      }
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams]
  );

  return (
    <List
      resource="admin/bunny-browser"
      perPage={100}
      pagination={false}
      filter={{ path }}
      filters={BUNNY_BROWSER_FILTERS}
      actions={<BunnyBrowserActions />}
      empty={false}
    >
      <BunnyBrowserListContent onNavigate={onNavigate} />
    </List>
  );
};

const BunnyDetailPreview = ({ record }) => {
  const previewUrl = record?.effective_url || record?.public_url || "";
  if (!isImageRecord(record) || !previewUrl) return null;

  return (
    <Card variant="outlined" sx={{ overflow: "hidden" }}>
      <CardContent>
        <Box
          component="img"
          src={previewUrl}
          alt={record?.name || record?.full_path || "Bunny object"}
          sx={{
            width: "100%",
            maxHeight: 420,
            objectFit: "contain",
            display: "block",
            backgroundColor: "#f3f9f9",
          }}
        />
      </CardContent>
    </Card>
  );
};

const BunnyBrowserShowContent = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.25}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
              <Box>
                <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#007073" }}>
                  {record.name || record.full_path}
                </Typography>
                <Typography sx={{ mt: 0.25, fontSize: 13, color: "#666", wordBreak: "break-all" }}>
                  {record.full_path}
                </Typography>
              </Box>
              <Chip
                label={record.content_type || "Object"}
                sx={{ backgroundColor: "#d8e6e6", color: "#262626", fontWeight: 700 }}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                component="a"
                href={record.public_url || record.effective_url || "#"}
                target="_blank"
                rel="noreferrer"
                variant="contained"
                endIcon={<OpenInNewOutlinedIcon />}
                disabled={!record.public_url && !record.effective_url}
              >
                Open object
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <BunnyDetailPreview record={record} />

      <Card variant="outlined">
        <CardContent>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#111827", mb: 1 }}>
            Object Details
          </Typography>
          <SimpleShowLayout>
            <RaTextField source="bucket" label="Storage Zone" />
            <RaTextField source="type" label="Type" />
            <RaTextField source="content_type" label="Content Type" />
            <RaTextField source="size_bytes" label="Size (bytes)" />
            <RaTextField source="etag" label="Checksum" />
            <RaTextField source="guid" label="Guid" />
            <RaTextField source="last_modified" label="Last Changed" />
            <RaTextField source="date_created" label="Date Created" />
          </SimpleShowLayout>
        </CardContent>
      </Card>
    </Stack>
  );
};

export const BunnyBrowserShow = () => (
  <Show>
    <BunnyBrowserShowContent />
  </Show>
);
